<script lang="ts">
  /**
   * Interactive board canvas.
   *
   * Built on Svelte Flow (@xyflow/svelte), which supplies pan/zoom, marquee and
   * multi-select, multi-node drag, snap-to-grid and resize handles, while every
   * item on the canvas stays a plain Svelte component rendered as real DOM.
   *
   * Persistence is write-behind: interactions mutate local node state
   * immediately and a debounced PATCH records the result.
   */
  import { onMount, onDestroy } from 'svelte';
  import {
    SvelteFlow,
    Background,
    BackgroundVariant,
    Controls,
    MiniMap,
    Panel,
    type Node,
    type Edge,
    type NodeTypes,
    type ResizeParams,
    type Viewport,
  } from '@xyflow/svelte';
  import '@xyflow/svelte/dist/style.css';
  import { toast } from 'svelte-sonner';
  import type { BoardItem, BoardItemStyle, BoardSummary, Project } from '../../lib/types';
  import { batchFetchProjectTasks, getCachedTasks } from '../../lib/stores/projectTasksCache';
  import {
    completeTask,
    deleteTask,
    duplicateTask,
    markTaskWontDo,
    moveTaskToProject,
    updateTask,
    updateTaskPriority,
  } from '../../lib/taskApi';
  import { openProjectModalForConvertFromTask, projectUpdated } from '../../lib/stores/ui';
  import type { Task, TaskPriority } from '../../lib/types';
  import {
    createBoardItem,
    createBoardItems,
    createLayoutPersister,
    deleteBoardItems,
    restoreBoardItems,
  } from '../../lib/boardApi';
  import { undoableAction, undoableSoftDelete } from '../../lib/undoManager';
  import { planPlacements, screenRectToCanvasRect } from '../../lib/boardPlacement';
  import { createBoardHistory, type BoardOp, type BoardRect } from '../../lib/boardHistory';
  import { getBoardHistoryShortcut } from '../../lib/boardHistoryKeyboard';
  import {
    setBoardActions,
    setBoardDisplay,
    setBoardTaskUi,
    type BoardDisplay,
  } from '../../lib/boardContext';
  import BoardProjectCardNode from './BoardProjectCardNode.svelte';
  import TextLabelNode from './TextLabelNode.svelte';
  import ShapeNode from './ShapeNode.svelte';
  import BoardProjectPicker from './BoardProjectPicker.svelte';
  import BoardStylePanel from './BoardStylePanel.svelte';
  import BoardTaskMenu from './BoardTaskMenu.svelte';
  import TaskDetailPopover from '../TaskDetailPopover.svelte';
  import { removeTaskAfterConversion } from '../../lib/taskActions';

  interface Props {
    board: BoardSummary;
    items: BoardItem[];
    projects: Project[];
    /**
     * Newly learned projects (added via the picker, restored via undo). The
     * board page ships only the projects already placed on it, so these are
     * unknown to ProjectRail — reporting them lets the page merge them in.
     */
    onProjectsKnown?: (projects: Project[]) => void;
  }

  let { board, items, projects, onProjectsKnown = () => {} }: Props = $props();

  const DEFAULT_CARD_SIZE = { width: 260, height: 256 };
  const DEFAULT_SHAPE_SIZE = { width: 180, height: 130 };
  const DEFAULT_TEXT_SIZE = { width: 180, height: 40 };
  const MIN_ZOOM = 0.2;
  const MAX_ZOOM = 2;
  /** Light snapping keeps hand-placed items visually aligned without feeling rigid. */
  const SNAP_GRID: [number, number] = [8, 8];
  /**
   * Hold the platform modifier and drag with any mouse button to pan the canvas
   * (the "hand tool"). Matches the library's own convention for zoomActivationKey
   * and multiSelectionKey, so Cmd/Ctrl + drag pans and Cmd/Ctrl + wheel zooms.
   */
  const PAN_ACTIVATION_KEY = navigator.userAgent.includes('Mac') ? 'Meta' : 'Control';

  const nodeTypes: NodeTypes = {
    project_card: BoardProjectCardNode as NodeTypes[string],
    text: TextLabelNode as NodeTypes[string],
    shape: ShapeNode as NodeTypes[string],
  };

  // The board identity is fixed for the lifetime of this component — an Inertia
  // navigation to another board remounts it.
  // svelte-ignore state_referenced_locally
  const persister = createLayoutPersister(board.id, {
    onError: () => toast.error('Could not save board changes'),
  });

  // Plain Map (not $state): a lookup table grown as cards are added, never rebuilt
  // from props, so it must not participate in reactivity.
  // svelte-ignore state_referenced_locally
  let projectsById = new Map(projects.map((p) => [p.id, p]));

  // Shared with node components via context so toggling it doesn't rewrite every node.
  // svelte-ignore state_referenced_locally
  const display = $state<BoardDisplay>({ showCompleted: false, zoom: board.viewport.zoom });
  setBoardDisplay(display);
  // Keeping these out of node `data` keeps each node structured-cloneable, which
  // is how Svelte Flow checks for accidentally deep-reactive node arrays.
  setBoardActions({
    onResizeStart: handleResizeStart,
    onResizeEnd: handleResizeEnd,
    onContentStart: handleContentStart,
    onContentChange: handleContentChange,
    onDelete: (id) => removeItems(nodes.filter((node) => node.id === id)),
  });

  let pickerOpen = $state(false);
  let historyVersion = $state(0);
  const history = createBoardHistory({ onChange: () => historyVersion++ });
  let isApplyingHistory = false;
  let dragStartPositions = new Map<string, { x: number; y: number }>();
  let resizeStartRects = new Map<string, BoardRect>();
  let contentStartValues = new Map<string, string>();
  let canvasEl = $state<HTMLDivElement | undefined>();
  // Mirrors the live viewport so new items can be placed in the visible area.
  // svelte-ignore state_referenced_locally
  let currentViewport = $state<Viewport>({ ...board.viewport });

  function defaultSizeFor(kind: BoardItem['kind']) {
    if (kind === 'shape') return DEFAULT_SHAPE_SIZE;
    if (kind === 'text') return DEFAULT_TEXT_SIZE;
    return DEFAULT_CARD_SIZE;
  }

  function toNode(item: BoardItem): Node {
    const size = defaultSizeFor(item.kind);
    return {
      id: item.id,
      type: item.kind,
      position: { x: item.x, y: item.y },
      width: item.width ?? size.width,
      height: item.height ?? size.height,
      zIndex: item.zIndex,
      data: {
        project: item.projectId ? projectsById.get(item.projectId) : undefined,
        content: item.content ?? '',
        style: item.style ?? {},
      },
    };
  }

  // Built once from the initial payload, then maintained incrementally. Rebuilding
  // from props on every change would clobber in-flight drag positions.
  // svelte-ignore state_referenced_locally
  let nodes = $state.raw<Node[]>(
    items
      .filter((item) => item.kind !== 'project_card' || projectsById.has(item.projectId!))
      .map(toNode),
  );
  let edges = $state.raw<Edge[]>([]);

  // ProjectModal updates Inertia data, but Svelte Flow keeps node data locally so
  // the board needs an explicit update to reflect edits without rebuilding layout.
  // eslint-disable-next-line momentium/no-unguarded-effect -- external store subscription for project edits
  $effect(() => {
    return projectUpdated.subscribe((updatedProject) => {
      if (!updatedProject || !projectsById.has(updatedProject.id)) return;

      rememberProjects([updatedProject]);
      nodes = nodes.map((node) =>
        (node.data.project as Project | undefined)?.id === updatedProject.id
          ? { ...node, data: { ...node.data, project: updatedProject } }
          : node,
      );
    });
  });

  /**
   * Records projects the canvas has learned about and reports them up so the
   * project rail can resolve them. The rail resolves its project from the
   * page's project list, which initially contains only placed projects — a
   * project added after load would otherwise open a rail that renders nothing.
   */
  function rememberProjects(learned: Project[]) {
    for (const project of learned) {
      if (!projectsById.has(project.id)) projectsById.set(project.id, project);
    }
    if (learned.length > 0) onProjectsKnown(learned);
  }

  let selectedNodes = $derived(nodes.filter((n) => n.selected));
  let singleSelection = $derived(selectedNodes.length === 1 ? selectedNodes[0] : null);
  let stylableSelection = $derived(
    singleSelection && singleSelection.type !== 'project_card' ? singleSelection : null,
  );
  let boardContextMenu = $state<{ node: Node; x: number; y: number } | null>(null);

  let placedProjectIds = $derived(
    new Set(
      nodes
        .filter((n) => n.type === 'project_card')
        .map((n) => (n.data.project as Project | undefined)?.id)
        .filter((id): id is string => !!id),
    ),
  );

  onMount(() => {
    // One request for every project card's tasks instead of one per card.
    const projectIds = items
      .filter((item) => item.kind === 'project_card' && item.projectId)
      .map((item) => item.projectId as string);
    if (projectIds.length > 0) {
      batchFetchProjectTasks(projectIds);
    }

    // A tab close or bfcache hide would otherwise drop debounced writes.
    const flushOnHide = () => {
      if (document.visibilityState === 'hidden') persister.flush();
    };
    document.addEventListener('visibilitychange', flushOnHide);
    return () => document.removeEventListener('visibilitychange', flushOnHide);
  });

  onDestroy(() => {
    persister.flush();
  });

  function handleNodeDragStart({ nodes: dragged }: { nodes: Node[] }) {
    dragStartPositions = new Map(
      dragged.map((node) => [node.id, { x: node.position.x, y: node.position.y }]),
    );
  }

  function handleNodeDragStop({ nodes: dragged }: { nodes: Node[] }) {
    const movedItems = dragged
      .map((node) => ({
        id: node.id,
        from: dragStartPositions.get(node.id),
        to: { x: node.position.x, y: node.position.y },
      }))
      .filter(
        (
          item,
        ): item is { id: string; from: { x: number; y: number }; to: { x: number; y: number } } =>
          !!item.from && (item.from.x !== item.to.x || item.from.y !== item.to.y),
      );
    if (movedItems.length > 0 && !isApplyingHistory)
      history.push({ type: 'move', items: movedItems });
    persister.queueItems(
      dragged.map((node) => ({ id: node.id, x: node.position.x, y: node.position.y })),
    );
    dragStartPositions.clear();
  }

  function handleResizeStart(id: string) {
    const node = nodes.find((candidate) => candidate.id === id);
    if (!node) return;
    resizeStartRects.set(id, {
      x: node.position.x,
      y: node.position.y,
      width: node.width ?? DEFAULT_CARD_SIZE.width,
      height: node.height ?? DEFAULT_CARD_SIZE.height,
    });
  }

  function handleResizeEnd(id: string, params: ResizeParams) {
    const from = resizeStartRects.get(id);
    const to: BoardRect = {
      x: params.x,
      y: params.y,
      width: params.width,
      height: params.height,
    };
    if (from && !isApplyingHistory && JSON.stringify(from) !== JSON.stringify(to))
      history.push({ type: 'resize', id, from, to });
    resizeStartRects.delete(id);
    persister.queueItems([{ id, ...to }]);
  }

  function handleContentStart(id: string) {
    const node = nodes.find((candidate) => candidate.id === id);
    if (node) contentStartValues.set(id, (node.data.content as string | undefined) ?? '');
  }

  function handleContentChange(id: string, content: string) {
    const node = nodes.find((n) => n.id === id);
    if (!node) return;
    const from = contentStartValues.get(id) ?? (node.data.content as string | undefined) ?? '';
    if (!isApplyingHistory && from !== content)
      history.push({ type: 'content', id, from, to: content });
    contentStartValues.delete(id);
    persister.queueItems([{ id, x: node.position.x, y: node.position.y, content }]);
  }

  function handleMove(_event: MouseEvent | TouchEvent | null, viewport: Viewport) {
    // Cards use the live zoom to replace tiny task rows with a readable project
    // summary while the user is navigating the canvas.
    display.zoom = viewport.zoom;
  }

  function handleMoveEnd(_event: MouseEvent | TouchEvent | null, viewport: Viewport) {
    display.zoom = viewport.zoom;
    currentViewport = viewport;
    persister.queueViewport(viewport);
  }

  function deleteMessage(removed: Node[]) {
    if (removed.length === 1) {
      const node = removed[0];
      if (node.type === 'project_card')
        return `Removed "${(node.data.project as Project)?.title ?? 'project'}" from board`;
      return node.type === 'text' ? 'Label deleted' : 'Shape deleted';
    }
    return removed.every((node) => node.type === 'project_card')
      ? `Removed ${removed.length} projects from board`
      : `${removed.length} items removed from board`;
  }

  function removeItems(removed: Node[]) {
    if (removed.length === 0) return;
    const ids = removed.map((node) => node.id);
    const operation: BoardOp = { type: 'delete', itemIds: ids };
    undoableSoftDelete({
      message: deleteMessage(removed),
      onOptimisticRemove: () => {
        nodes = nodes.filter((node) => !ids.includes(node.id));
      },
      onOptimisticRestore: () => {
        history.remove(operation);
        nodes = [
          ...nodes,
          ...removed.filter((node) => !nodes.some((existing) => existing.id === node.id)),
        ].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
      },
      onDelete: async () => {
        await deleteBoardItems(board.id, ids);
      },
      onRestore: async () => {
        const restored = await restoreBoardItems(board.id, ids);
        rememberProjects(restored.projects);
        const restoredProjectIds = restored.items
          .filter((item) => item.kind === 'project_card' && item.projectId)
          .map((item) => item.projectId as string);
        if (restoredProjectIds.length) batchFetchProjectTasks(restoredProjectIds);
        nodes = [
          ...nodes.filter((node) => !ids.includes(node.id)),
          ...restored.items.map(toNode),
        ].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
        if (restored.skipped.length)
          toast.error('Some items could not be restored because the board is full');
      },
    });
    if (!isApplyingHistory) history.push(operation);
  }

  function handleDelete({ nodes: deleted }: { nodes: Node[] }) {
    // Svelte Flow has already removed these nodes locally; removeItems supplies
    // rollback and the server-side soft delete as one undoable gesture.
    removeItems(deleted);
  }

  /** Live rects of the items already on the canvas, in canvas coordinates. */
  function occupiedRects(): BoardRect[] {
    return nodes.map((n) => ({
      x: n.position.x,
      y: n.position.y,
      width: n.width ?? DEFAULT_CARD_SIZE.width,
      height: n.height ?? DEFAULT_CARD_SIZE.height,
    }));
  }

  /**
   * The top-left panel (toolbar + open project picker) in canvas coordinates.
   *
   * While the picker is open it floats over the top-left of the viewport, right
   * where the first placement slot lands. A card dropped underneath it is
   * covered by the panel: its hover-revealed header actions never appear (and
   * its top-right corner is hidden), so the new card looks broken until the
   * picker is closed or the page is refreshed. Treating the panel as occupied
   * pushes new items clear of it.
   */
  function topLeftPanelRect(): BoardRect | null {
    if (!pickerOpen || !canvasEl) return null;
    const panel = canvasEl?.querySelector('.svelte-flow__panel.top.left') as HTMLElement | null;
    if (!panel) return null;
    // Client coordinates are relative to the window, but the viewport transform
    // is relative to the flow container (which sits below the page header) —
    // the container offset must be removed before converting to canvas space.
    return screenRectToCanvasRect(
      panel.getBoundingClientRect(),
      canvasEl.getBoundingClientRect(),
      currentViewport,
    );
  }

  function nextPlacement(size: { width: number; height: number }) {
    const rect = canvasEl?.getBoundingClientRect();
    const occupied = occupiedRects();
    const keepOut = topLeftPanelRect();
    if (keepOut) occupied.push(keepOut);
    return planPlacements(1, size, occupied, currentViewport, {
      width: rect?.width ?? 1200,
      height: rect?.height ?? 800,
    })[0];
  }

  async function addProjects(projectIds: string[]) {
    const occupied = occupiedRects();
    const keepOut = topLeftPanelRect();
    if (keepOut) occupied.push(keepOut);
    const placements = planPlacements(
      projectIds.length,
      DEFAULT_CARD_SIZE,
      occupied,
      currentViewport,
      {
        width: canvasEl?.getBoundingClientRect().width ?? 1200,
        height: canvasEl?.getBoundingClientRect().height ?? 800,
      },
    );
    const inputs = projectIds.map((projectId, index) => ({
      kind: 'project_card' as const,
      projectId,
      x: placements[index].x,
      y: placements[index].y,
      width: placements[index].width,
      height: placements[index].height,
    }));

    try {
      const result = await createBoardItems(board.id, inputs);
      const createdIds = result.created.map(({ item }) => item.id);
      const createdProjects = result.created.map(({ project }) => project);
      rememberProjects(createdProjects);
      if (createdProjects.length > 0) {
        batchFetchProjectTasks(createdProjects.map((project) => project.id));
        nodes = [...nodes, ...result.created.map(({ item }) => toNode(item))];
      }

      if (result.skipped.length > 0) {
        toast.info(
          `Added ${createdIds.length} · ${result.skipped.length} skipped (${result.skipped
            .map((entry) => entry.reason)
            .join(', ')})`,
        );
      }

      if (createdIds.length > 0) {
        const operation: BoardOp = { type: 'create', itemIds: createdIds };
        if (!isApplyingHistory) history.push(operation);
        undoableAction({
          message: `Added ${createdIds.length} project${createdIds.length === 1 ? '' : 's'}`,
          onCommit: () => {},
          onOptimisticUndo: () => {
            history.remove(operation);
            nodes = nodes.filter((node) => !createdIds.includes(node.id));
          },
          onUndo: async () => {
            await deleteBoardItems(board.id, createdIds);
          },
          onUndoError: () => {
            const createdItems = result.created.map(({ item }) => toNode(item));
            nodes = [
              ...nodes,
              ...createdItems.filter((item) => !nodes.some((node) => node.id === item.id)),
            ];
          },
          undoErrorMessage: 'Failed to undo project add',
        });
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not add projects');
      throw error;
    }
  }

  function recordCreate(itemId: string, message: string) {
    const createdNode = nodes.find((node) => node.id === itemId);
    if (!createdNode) return;
    const operation: BoardOp = { type: 'create', itemIds: [itemId] };
    if (!isApplyingHistory) history.push(operation);
    undoableAction({
      message,
      onCommit: () => {},
      onOptimisticUndo: () => {
        history.remove(operation);
        nodes = nodes.filter((node) => node.id !== itemId);
      },
      onUndo: async () => {
        await deleteBoardItems(board.id, [itemId]);
      },
      onUndoError: () => {
        if (!nodes.some((node) => node.id === itemId)) nodes = [...nodes, createdNode];
      },
      undoErrorMessage: 'Failed to undo item add',
    });
  }

  async function addText() {
    const placement = nextPlacement(DEFAULT_TEXT_SIZE);
    try {
      const { item } = await createBoardItem(board.id, {
        kind: 'text',
        x: placement.x,
        y: placement.y,
        width: placement.width,
        height: placement.height,
        content: 'New label',
        style: {
          fontSize: '16',
          textColor: '#1f2937',
          fontWeight: '500',
          fontStyle: 'normal',
          textAlign: 'center',
          verticalAlign: 'center',
        },
      });
      nodes = [...nodes, toNode(item)];
      recordCreate(item.id, 'Added label');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not add label');
    }
  }

  async function addShape() {
    const placement = nextPlacement(DEFAULT_SHAPE_SIZE);
    try {
      const { item } = await createBoardItem(board.id, {
        kind: 'shape',
        x: placement.x,
        y: placement.y,
        width: placement.width,
        height: placement.height,
        style: { shapeVariant: 'rectangle', fill: '#dbeafe', stroke: '#93c5fd' },
      });
      nodes = [...nodes, toNode(item)];
      recordCreate(item.id, 'Added shape');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not add shape');
    }
  }

  function applyStyle(patch: BoardItemStyle) {
    const target = stylableSelection;
    if (!target) return;

    const previousStyle = { ...(target.data.style as BoardItemStyle) };
    const nextStyle = { ...previousStyle, ...patch };
    nodes = nodes.map((n) =>
      n.id === target.id ? { ...n, data: { ...n.data, style: nextStyle } } : n,
    );
    if (!isApplyingHistory && JSON.stringify(previousStyle) !== JSON.stringify(nextStyle))
      history.push({ type: 'style', id: target.id, from: previousStyle, to: nextStyle });
    persister.queueItems([
      { id: target.id, x: target.position.x, y: target.position.y, style: nextStyle },
    ]);
  }

  // === Task overlays ===
  //
  // The actions menu and detail popover are rendered here rather than inside a
  // card because Svelte Flow transforms the viewport, which breaks `position:
  // fixed` for descendants and clips them to the card's overflow.

  let menuTask = $state<Task | null>(null);
  let menuAnchorEl = $state<HTMLElement | null>(null);
  let menuProjectId = $state<string | null>(null);

  let detailTask = $state<Task | null>(null);
  let detailAnchorEl = $state<HTMLElement | null>(null);
  let detailProjectId = $state<string | null>(null);

  setBoardTaskUi({
    get openMenuTaskId() {
      return menuTask?.id ?? null;
    },
    toggleMenu(task, projectId, anchorEl) {
      if (menuTask?.id === task.id) {
        closeMenu();
        return;
      }
      closeDetail();
      menuTask = task;
      menuProjectId = projectId;
      menuAnchorEl = anchorEl;
    },
    openDetail(task, projectId, anchorEl) {
      if (detailTask?.id === task.id) {
        closeDetail();
        return;
      }
      closeMenu();
      // Re-read from the cache so an edit made elsewhere isn't clobbered by a
      // stale copy captured when the card last rendered.
      const latest = getCachedTasks(projectId).tasks.find((t) => t.id === task.id) ?? task;
      detailTask = latest;
      detailProjectId = projectId;
      detailAnchorEl = anchorEl;
    },
  });

  function closeMenu() {
    menuTask = null;
    menuAnchorEl = null;
    menuProjectId = null;
  }

  function closeDetail() {
    detailTask = null;
    detailAnchorEl = null;
    // detailProjectId is deliberately left set: TaskDetailPopover flushes pending
    // title/notes edits as it closes, and that save still needs the project id.
    // It is overwritten on the next open.
  }

  /** Tasks currently cached for a project — the base state every mutation needs. */
  function tasksFor(projectId: string): Task[] {
    return getCachedTasks(projectId).tasks;
  }

  async function handleSetPriority(task: Task, priority: TaskPriority) {
    const projectId = menuProjectId;
    closeMenu();
    if (!projectId || task.priority === priority) return;

    const result = await updateTaskPriority(projectId, task.id, priority, tasksFor(projectId));
    if (!result.success) toast.error('Could not update priority');
  }

  async function handleDuplicateTask(task: Task, projectId: string | null = menuProjectId) {
    closeMenu();
    if (!projectId) return;
    await duplicateTask(projectId, task.id, tasksFor(projectId));
  }

  async function handleMarkWontDo(
    task: Task,
    projectId: string | null = menuProjectId,
    pendingUpdates?: Partial<Task>,
  ) {
    closeMenu();
    closeDetail();
    if (!projectId) return;

    // pendingUpdates carries unsaved popover edits so they persist in the same call.
    const result = await markTaskWontDo(projectId, task.id, tasksFor(projectId), pendingUpdates);
    if (!result.success) toast.error('Could not update task');
  }

  async function handleDeleteTask(task: Task, projectId: string | null = menuProjectId) {
    closeMenu();
    closeDetail();
    if (!projectId) return;

    // deleteTask surfaces its own undo toast.
    await deleteTask(projectId, task.id, tasksFor(projectId));
  }

  async function handleToggleComplete(
    task: Task,
    projectId: string | null = detailProjectId,
    pendingUpdates?: Partial<Task>,
  ) {
    closeDetail();
    if (!projectId) return;

    const result = await completeTask(projectId, task.id, tasksFor(projectId), pendingUpdates);
    if (!result.success) toast.error('Could not update task');
  }

  async function handleDetailUpdate(task: Task, updates: Partial<Task>) {
    const projectId = detailProjectId;
    if (!projectId) return;

    const result = await updateTask(projectId, task.id, updates, tasksFor(projectId));
    if (!result.success) {
      toast.error('Could not save changes');
      return;
    }
    if (detailTask?.id === task.id) {
      detailTask = result.tasks.find((t) => t.id === task.id) ?? detailTask;
    }
  }

  /** Move from the menu — its in-menu project search or the recent-move shortcut. */
  async function handleMoveToProject(task: Task, targetProjectId: string, targetTitle: string) {
    const sourceProjectId = menuProjectId;
    closeMenu();
    if (!sourceProjectId) return;

    await moveTaskToProject(sourceProjectId, targetProjectId, task.id, tasksFor(sourceProjectId), {
      targetProjectTitle: targetTitle,
    });
  }

  function handleConvertToProject(task: Task) {
    const projectId = menuProjectId;
    closeMenu();
    if (!projectId) return;

    // The original task is deleted once the new project has been created.
    openProjectModalForConvertFromTask(task.title, task.notes || '', () =>
      removeTaskAfterConversion({ ...task, projectId }),
    );
  }

  /** A click anywhere outside the menu dismisses it, matching ProjectRail. */
  function handleWindowClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (menuTask && !target?.closest('[data-board-task-menu]')) closeMenu();
    if (boardContextMenu && !target?.closest('[data-board-context-menu]')) boardContextMenu = null;
  }

  function handleNodeContextMenu({ node, event }: { node: Node; event: MouseEvent }) {
    event.preventDefault();
    nodes = nodes.map((candidate) => ({ ...candidate, selected: candidate.id === node.id }));
    boardContextMenu = {
      node,
      x: Math.min(event.clientX, window.innerWidth - 190),
      y: Math.min(event.clientY, window.innerHeight - 250),
    };
  }

  async function duplicateNode() {
    const target = boardContextMenu?.node;
    boardContextMenu = null;
    if (!target || target.type === 'project_card') return;

    try {
      const { item } = await createBoardItem(board.id, {
        kind: target.type as 'text' | 'shape',
        x: target.position.x + 24,
        y: target.position.y + 24,
        width: target.width ?? defaultSizeFor(target.type as BoardItem['kind']).width,
        height: target.height ?? defaultSizeFor(target.type as BoardItem['kind']).height,
        content: (target.data.content as string | undefined) ?? '',
        style: { ...(target.data.style as BoardItemStyle) },
      });
      nodes = [...nodes, toNode(item)];
      recordCreate(item.id, target.type === 'text' ? 'Duplicated label' : 'Duplicated shape');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not duplicate item');
    }
  }

  function changeLayer(direction: 'backward' | 'back' | 'forward' | 'front') {
    const target = boardContextMenu?.node;
    boardContextMenu = null;
    if (!target) return;

    const ordered = [...nodes].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
    const index = ordered.findIndex((node) => node.id === target.id);
    if (index < 0) return;

    const currentTarget = ordered[index];
    const targetZIndex = currentTarget.zIndex ?? 0;
    let changes: { id: string; from: number; to: number }[] = [];
    if (direction === 'back' && index > 0) {
      const other = ordered[0];
      changes = [{ id: target.id, from: targetZIndex, to: (other.zIndex ?? 0) - 1 }];
    } else if (direction === 'front' && index < ordered.length - 1) {
      const other = ordered[ordered.length - 1];
      changes = [{ id: target.id, from: targetZIndex, to: (other.zIndex ?? 0) + 1 }];
    } else if (direction === 'backward' && index > 0) {
      const other = ordered[index - 1];
      changes = [
        { id: target.id, from: targetZIndex, to: other.zIndex ?? 0 },
        { id: other.id, from: other.zIndex ?? 0, to: targetZIndex },
      ];
    } else if (direction === 'forward' && index < ordered.length - 1) {
      const other = ordered[index + 1];
      changes = [
        { id: target.id, from: targetZIndex, to: other.zIndex ?? 0 },
        { id: other.id, from: other.zIndex ?? 0, to: targetZIndex },
      ];
    }
    if (changes.length === 0) return;

    const zIndexes = new Map(changes.map((change) => [change.id, change.to]));
    // Svelte Flow renders selected nodes above unselected nodes regardless of their z-index.
    // Clear selection so the new layer order is immediately visible after the menu closes.
    nodes = nodes.map((node) => ({
      ...node,
      selected: false,
      ...(zIndexes.has(node.id) ? { zIndex: zIndexes.get(node.id) } : {}),
    }));
    if (!isApplyingHistory) history.push({ type: 'layer', items: changes });
    persister.queueItems(
      changes.map((change) => {
        const node = nodes.find((candidate) => candidate.id === change.id)!;
        return { id: node.id, x: node.position.x, y: node.position.y, zIndex: change.to };
      }),
    );

    // Svelte Flow applies its context-menu selection after this handler returns.
    // Clear it in the next task so its selected-node elevation cannot mask the layer change.
    window.setTimeout(() => {
      nodes = nodes.map((node) => ({ ...node, selected: false }));
    }, 0);
  }

  function deleteContextNode() {
    const target = boardContextMenu?.node;
    boardContextMenu = null;
    if (target) removeItems([target]);
  }

  function deleteSelection() {
    const target = singleSelection;
    if (target) removeItems([target]);
  }

  async function applyHistoryOperation(operation: BoardOp, undo: boolean) {
    const target = undo ? 'from' : 'to';
    if (operation.type === 'move') {
      const positions = new Map(operation.items.map((item) => [item.id, item[target]]));
      nodes = nodes.map((node) => {
        const position = positions.get(node.id);
        return position ? { ...node, position } : node;
      });
      persister.queueItems(
        operation.items.map((item) => ({ id: item.id, x: item[target].x, y: item[target].y })),
      );
      return;
    }

    if (operation.type === 'resize') {
      const rect = operation[target];
      nodes = nodes.map((node) =>
        node.id === operation.id
          ? { ...node, position: { x: rect.x, y: rect.y }, width: rect.width, height: rect.height }
          : node,
      );
      persister.queueItems([{ id: operation.id, ...rect }]);
      return;
    }

    if (operation.type === 'style') {
      const style = operation[target];
      const node = nodes.find((candidate) => candidate.id === operation.id);
      if (!node) return;
      nodes = nodes.map((candidate) =>
        candidate.id === operation.id
          ? { ...candidate, data: { ...candidate.data, style } }
          : candidate,
      );
      persister.queueItems([{ id: operation.id, x: node.position.x, y: node.position.y, style }]);
      return;
    }

    if (operation.type === 'layer') {
      const zIndexes = new Map(operation.items.map((item) => [item.id, item[target]]));
      nodes = nodes.map((node) =>
        zIndexes.has(node.id) ? { ...node, zIndex: zIndexes.get(node.id) } : node,
      );
      persister.queueItems(
        operation.items.map((item) => {
          const node = nodes.find((candidate) => candidate.id === item.id)!;
          return { id: item.id, x: node.position.x, y: node.position.y, zIndex: item[target] };
        }),
      );
      return;
    }

    if (operation.type === 'content') {
      const content = operation[target];
      const node = nodes.find((candidate) => candidate.id === operation.id);
      if (!node) return;
      nodes = nodes.map((candidate) =>
        candidate.id === operation.id
          ? { ...candidate, data: { ...candidate.data, content } }
          : candidate,
      );
      persister.queueItems([{ id: operation.id, x: node.position.x, y: node.position.y, content }]);
      return;
    }

    if ((operation.type === 'delete' && undo) || (operation.type === 'create' && !undo)) {
      const restored = await restoreBoardItems(board.id, operation.itemIds);
      rememberProjects(restored.projects);
      nodes = [
        ...nodes.filter((node) => !operation.itemIds.includes(node.id)),
        ...restored.items.map(toNode),
      ].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
      return;
    }

    await deleteBoardItems(board.id, operation.itemIds);
    nodes = nodes.filter((node) => !operation.itemIds.includes(node.id));
  }

  async function changeHistory(direction: 'undo' | 'redo') {
    // Reading this reactive value keeps the toolbar and keyboard state coupled
    // to the module's otherwise plain, framework-agnostic history object.
    void historyVersion;
    if (historyBusy) return;
    const undo = direction === 'undo';
    const operation = undo ? history.undo() : history.redo();
    if (!operation) return;
    historyBusy = true;
    isApplyingHistory = true;
    try {
      await applyHistoryOperation(operation, undo);
    } catch (error) {
      if (undo) history.rollbackUndo(operation);
      else history.rollbackRedo(operation);
      toast.error(error instanceof Error ? error.message : 'Could not apply canvas history');
    } finally {
      isApplyingHistory = false;
      historyBusy = false;
    }
  }

  let canvasHovered = $state(false);
  let historyBusy = $state(false);

  function handleKeydown(event: KeyboardEvent) {
    const shortcut = getBoardHistoryShortcut(event, {
      canvasHovered,
      overlayOpen: !!(menuTask || detailTask),
      target: event.target,
    });
    if (!shortcut) return;
    event.preventDefault();
    void changeHistory(shortcut);
  }
</script>

<div
  class="board-canvas"
  bind:this={canvasEl}
  onmouseenter={() => (canvasHovered = true)}
  onmouseleave={() => (canvasHovered = false)}
  role="application"
>
  <SvelteFlow
    bind:nodes
    bind:edges
    {nodeTypes}
    initialViewport={board.viewport}
    minZoom={MIN_ZOOM}
    maxZoom={MAX_ZOOM}
    snapGrid={SNAP_GRID}
    selectionOnDrag
    panOnDrag={[1, 2]}
    panActivationKey={PAN_ACTIVATION_KEY}
    deleteKey={['Delete', 'Backspace']}
    onnodedragstart={handleNodeDragStart}
    onnodedragstop={handleNodeDragStop}
    onmoveend={handleMoveEnd}
    ondelete={handleDelete}
    onmove={handleMove}
    onnodecontextmenu={handleNodeContextMenu}
  >
    <Background variant={BackgroundVariant.Dots} gap={16} size={1} bgColor="#fafafa" />
    <Controls position="bottom-right" />
    <MiniMap position="bottom-left" pannable zoomable />

    <Panel position="top-left">
      <div class="toolbar">
        <div class="toolbar-group">
          <button class="tool-btn primary" type="button" onclick={() => (pickerOpen = !pickerOpen)}>
            + Project
          </button>
          <button class="tool-btn" type="button" onclick={addText}>+ Text</button>
          <button class="tool-btn" type="button" onclick={addShape}>+ Shape</button>
        </div>

        <div class="toolbar-group history-controls">
          <button
            class="tool-btn"
            type="button"
            disabled={historyBusy || (historyVersion >= 0 && !history.canUndo)}
            title="Undo (Cmd/Ctrl+Z)"
            onclick={() => void changeHistory('undo')}>↶ Undo</button
          >
          <button
            class="tool-btn"
            type="button"
            disabled={historyBusy || (historyVersion >= 0 && !history.canRedo)}
            title="Redo (Cmd/Ctrl+Shift+Z)"
            onclick={() => void changeHistory('redo')}>↷ Redo</button
          >
        </div>

        <label class="toolbar-toggle">
          <input type="checkbox" bind:checked={display.showCompleted} />
          Show completed tasks
        </label>
      </div>

      {#if pickerOpen}
        <div class="picker-anchor">
          <BoardProjectPicker
            {placedProjectIds}
            onAdd={addProjects}
            onClose={() => (pickerOpen = false)}
          />
        </div>
      {/if}
    </Panel>

    {#if stylableSelection}
      <Panel position="top-right">
        <BoardStylePanel
          kind={stylableSelection.type as 'text' | 'shape'}
          style={stylableSelection.data.style as BoardItemStyle}
          onChange={applyStyle}
          onDelete={deleteSelection}
        />
      </Panel>
    {/if}

    {#if nodes.length === 0}
      <Panel position="top-center">
        <div class="empty-hint">
          This board is empty. Add a project card, a label, or a shape to get started.
        </div>
      </Panel>
    {/if}
  </SvelteFlow>
</div>

{#if boardContextMenu}
  <div
    class="board-context-menu"
    data-board-context-menu
    role="menu"
    aria-label="Board item actions"
    style="left: {boardContextMenu.x}px; top: {boardContextMenu.y}px;"
  >
    <button
      type="button"
      role="menuitem"
      disabled={boardContextMenu.node.type === 'project_card'}
      title={boardContextMenu.node.type === 'project_card'
        ? 'A project can appear only once on a board'
        : undefined}
      onclick={duplicateNode}>Duplicate</button
    >
    <button type="button" role="menuitem" onclick={deleteContextNode}>Delete</button>
    <hr />
    <button type="button" role="menuitem" onclick={() => changeLayer('backward')}
      >Send Backward</button
    >
    <button type="button" role="menuitem" onclick={() => changeLayer('back')}>Send to Back</button>
    <button type="button" role="menuitem" onclick={() => changeLayer('forward')}
      >Bring Forward</button
    >
    <button type="button" role="menuitem" onclick={() => changeLayer('front')}
      >Bring to Front</button
    >
  </div>
{/if}

<svelte:window onclick={handleWindowClick} onkeydown={handleKeydown} />

<!--
  Task overlays live outside <SvelteFlow> on purpose: the viewport's CSS
  transform would otherwise break their fixed positioning and the card's
  overflow would clip them.
-->
<BoardTaskMenu
  task={menuTask}
  anchorEl={menuAnchorEl}
  onSetPriority={handleSetPriority}
  onDuplicate={handleDuplicateTask}
  onMarkWontDo={handleMarkWontDo}
  onDelete={handleDeleteTask}
  onMoveToProject={handleMoveToProject}
  onConvertToProject={handleConvertToProject}
  onClose={closeMenu}
  currentProjectId={menuProjectId}
/>

<TaskDetailPopover
  task={detailTask}
  anchorEl={detailAnchorEl}
  project={detailProjectId ? (projectsById.get(detailProjectId) ?? null) : null}
  onClose={closeDetail}
  onUpdate={handleDetailUpdate}
  onDelete={(task) => handleDeleteTask(task, detailProjectId)}
  onMarkWontDo={(task, pending) => handleMarkWontDo(task, detailProjectId, pending)}
  onToggleComplete={(task, pending) => handleToggleComplete(task, detailProjectId, pending)}
/>

<style>
  .board-canvas {
    width: 100%;
    height: 100%;
    min-height: 0;
  }

  .toolbar {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.375rem 0.5rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  .toolbar-group {
    display: flex;
    gap: 0.25rem;
  }

  .tool-btn {
    padding: 0.1875rem 0.5rem;
    font-size: 0.6875rem;
    font-weight: 600;
    font-family: inherit;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
    background: white;
    color: #374151;
    cursor: pointer;
    white-space: nowrap;
  }

  .tool-btn:hover {
    background: #f9fafb;
  }

  .tool-btn.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: white;
  }

  .tool-btn.primary:hover {
    background: #1d4ed8;
  }

  .history-controls {
    border-left: 1px solid #e5e7eb;
    padding-left: 0.375rem;
  }

  .tool-btn:disabled {
    color: #9ca3af;
    background: #f9fafb;
    cursor: not-allowed;
  }

  .toolbar-toggle {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.625rem;
    color: #6b7280;
    white-space: nowrap;
    cursor: pointer;
  }

  .picker-anchor {
    margin-top: 0.375rem;
  }

  .board-context-menu {
    position: fixed;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    min-width: 11rem;
    padding: 0.25rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
  }

  .board-context-menu button {
    padding: 0.375rem 0.5rem;
    border: 0;
    border-radius: 0.25rem;
    background: transparent;
    color: #374151;
    font: inherit;
    font-size: 0.75rem;
    text-align: left;
    cursor: pointer;
  }

  .board-context-menu button:hover:not(:disabled) {
    background: #f3f4f6;
  }

  .board-context-menu button:disabled {
    color: #9ca3af;
    cursor: not-allowed;
  }

  .board-context-menu hr {
    width: 100%;
    margin: 0.25rem 0;
    border: 0;
    border-top: 1px solid #e5e7eb;
  }

  .empty-hint {
    padding: 0.5rem 0.75rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    font-size: 0.6875rem;
    color: #6b7280;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
</style>
